<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-database-check</v-icon>
            Data Consistency Check
          </v-card-title>

          <v-card-subtitle>
            Compare orders and customer data between Database, Firebase, and Invoices
          </v-card-subtitle>

          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="selectedDate"
                      label="Select Date (Start of Week)"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="selectedDate"
                    @input="dateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col class="text-right">
                <v-btn
                  color="info"
                  :loading="loadingReport"
                  :disabled="!selectedDate || loadingFix"
                  @click="runReportOnly"
                  large
                  class="mr-2"
                >
                  <v-icon left>mdi-file-document-outline</v-icon>
                  Report Only
                </v-btn>
                <v-btn
                  color="warning"
                  :loading="loadingFix"
                  :disabled="!selectedDate || loadingReport"
                  @click="confirmAndFix"
                  large
                >
                  <v-icon left>mdi-wrench</v-icon>
                  Check & Fix Issues
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Results Section -->
    <v-row v-if="results">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left :color="hasIssues ? 'error' : 'success'">
              {{ hasIssues ? 'mdi-alert-circle' : 'mdi-check-circle' }}
            </v-icon>
            Check Results for {{ selectedDate }}
            <v-spacer></v-spacer>
            <v-chip v-if="isCachedResults" color="grey lighten-2" small>
              <v-icon left x-small>mdi-cached</v-icon>
              Cached {{ formatCacheTime(cachedTimestamp) }}
            </v-chip>
          </v-card-title>

          <v-card-text>
            <!-- Summary Cards -->
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-card color="blue lighten-5" outlined>
                  <v-card-title class="text-h6">Line Items</v-card-title>
                  <v-card-text>
                    <div class="text-h4">{{ results.lineItemUpdatedCount }}</div>
                    <div class="text-body-2">
                      Original: {{ results.lineItemOriginalCount }}
                      <br/>
                      Difference: {{ results.lineItemDifference }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <v-card color="green lighten-5" outlined>
                  <v-card-title class="text-h6">Database Meals</v-card-title>
                  <v-card-text>
                    <div class="text-h4">{{ results.mealCountUpdated }}</div>
                    <div class="text-body-2">
                      Original: {{ results.mealCountOriginal }}
                      <br/>
                      Difference: {{ results.mealCountDiff }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <v-card color="orange lighten-5" outlined>
                  <v-card-title class="text-h6">Invoice Meals</v-card-title>
                  <v-card-text>
                    <div class="text-h4">{{ results.invoiceMealCount }}</div>
                    <div class="text-body-2">Invoice Count</div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <v-card color="purple lighten-5" outlined>
                  <v-card-title class="text-h6">Firebase Meals</v-card-title>
                  <v-card-text>
                    <div class="text-h4">{{ results.firebaseOrdersMealCount }}</div>
                    <div class="text-body-2">Firebase Count</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Customer Sync Section -->
            <v-row class="mt-4" v-if="results.customerSyncInconsistencies !== undefined">
              <v-col cols="12">
                <v-card color="cyan lighten-5" outlined>
                  <v-card-title class="text-h6">
                    <v-icon left>mdi-account-sync</v-icon>
                    Customer Sync Status
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" sm="4">
                        <div class="text-h4">{{ results.totalCustomersChecked || 'N/A' }}</div>
                        <div class="text-body-2">Customers Checked</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="text-h4" :class="customerSyncColor">
                          {{ results.customerSyncInconsistencies }}
                        </div>
                        <div class="text-body-2">
                          {{ results.customerSyncReportOnly ? 'Found' : 'Fixed' }} Inconsistencies
                        </div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <v-chip
                          :color="results.customerSyncInconsistencies > 0 ? 'warning' : 'success'"
                          text-color="white"
                        >
                          <v-icon left small>
                            {{ results.customerSyncInconsistencies > 0 ? 'mdi-alert' : 'mdi-check' }}
                          </v-icon>
                          {{ results.customerSyncInconsistencies > 0 ? 'Issues Found' : 'All Synced' }}
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Line Item Attribute Issues Section -->
            <v-row v-if="results.lineItemAttributeDetails && results.lineItemAttributeDetails.length > 0" class="mt-4">
              <v-col cols="12">
                <v-alert type="warning" prominent>
                  <v-row align="center">
                    <v-col class="grow">
                      <div class="text-h6">
                        Line Item Attribute Issues
                      </div>
                      <div>
                        {{ results.lineItemAttributeDetails.length }} line items have mismatched customer attributes
                      </div>
                    </v-col>
                  </v-row>
                </v-alert>

                <v-expansion-panels class="mt-4">
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div>
                        <v-icon left color="warning">mdi-format-list-bulleted-square</v-icon>
                        View Line Item Attribute Issues ({{ results.lineItemAttributeDetails.length }})
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-data-table
                        :headers="lineItemAttributeHeaders"
                        :items="formattedLineItemAttributeDetails"
                        class="mt-4"
                        dense
                      >
                        <template v-slot:item.uid="{ item }">
                          <div class="d-flex align-center">
                            <v-btn
                              x-small
                              :color="uidFilter === item.uid ? 'accent' : 'secondary'"
                              @click="filterByUid(item.uid)"
                              class="mr-2"
                              :title="uidFilter === item.uid ? 'Clear filter' : 'Filter by UID'"
                            >
                              <v-icon x-small>mdi-filter</v-icon>
                            </v-btn>
                            <code>{{ item.uid }}</code>
                          </div>
                        </template>
                        <template v-slot:item.attribute="{ item }">
                          <v-chip small color="primary" text-color="white">
                            {{ item.attribute }}
                          </v-chip>
                        </template>
                        <template v-slot:item.values="{ item }">
                          <div>
                            <div class="text-caption">Customer: {{ formatValue(item.customerValue) }}</div>
                            <div class="text-caption">Line Item: {{ formatValue(item.lineItemValue) }}</div>
                          </div>
                        </template>
                        <template v-slot:item.type="{ item }">
                          <v-chip
                            small
                            :color="item.type === 'original' ? 'orange' : 'blue'"
                            text-color="white"
                          >
                            {{ item.type }}
                          </v-chip>
                        </template>
                        <template v-slot:item.actions="{ item }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="{ name: 'Customer', params: { id: item.uid } }"
                            class="mr-1"
                          >
                            <v-icon x-small>mdi-account</v-icon>
                          </v-btn>
                          <v-btn
                            x-small
                            color="info"
                            :to="getOrdersLink(item)"
                          >
                            <v-icon x-small>mdi-receipt</v-icon>
                          </v-btn>
                        </template>
                      </v-data-table>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>

            <!-- Customer Sync Issues Section -->
            <v-row v-if="results.customerSyncDetails && results.customerSyncDetails.length > 0" class="mt-4">
              <v-col cols="12">
                <v-alert type="info" prominent>
                  <v-row align="center">
                    <v-col class="grow">
                      <div class="text-h6">
                        Customer Sync Issues
                      </div>
                      <div>
                        {{ results.customerSyncDetails.length }} customers have Firebase-PostgreSQL sync inconsistencies
                      </div>
                    </v-col>
                  </v-row>
                </v-alert>

                <v-expansion-panels class="mt-4">
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div>
                        <v-icon left color="info">mdi-account-sync</v-icon>
                        View Customer Sync Details ({{ results.customerSyncDetails.length }})
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-data-table
                        :headers="customerSyncHeaders"
                        :items="formattedCustomerSyncDetails"
                        class="mt-4"
                        dense
                      >
                        <template v-slot:item.uid="{ item }">
                          <div class="d-flex align-center">
                            <v-btn
                              x-small
                              :color="uidFilter === item.uid ? 'accent' : 'secondary'"
                              @click="filterByUid(item.uid)"
                              class="mr-2"
                              :title="uidFilter === item.uid ? 'Clear filter' : 'Filter by UID'"
                            >
                              <v-icon x-small>mdi-filter</v-icon>
                            </v-btn>
                            <code>{{ item.uid }}</code>
                          </div>
                        </template>
                        <template v-slot:item.details="{ item }">
                          <div v-if="item.details && item.details.length > 0">
                            <div v-if="item.details.length <= 3">
                              <v-chip
                                v-for="(diff, index) in item.details"
                                :key="index"
                                small
                                color="info"
                                text-color="white"
                                class="ma-1"
                              >
                                {{ formatDifference(diff) }}
                              </v-chip>
                            </div>
                            <div v-else>
                              <div v-if="!item.expanded">
                                <v-chip
                                  v-for="(diff, index) in item.details.slice(0, 3)"
                                  :key="index"
                                  small
                                  color="info"
                                  text-color="white"
                                  class="ma-1"
                                >
                                  {{ formatDifference(diff) }}
                                </v-chip>
                                <v-btn
                                  small
                                  text
                                  color="primary"
                                  @click="toggleDifferences(item)"
                                >
                                  +{{ item.details.length - 3 }} more
                                  <v-icon small>mdi-chevron-down</v-icon>
                                </v-btn>
                              </div>
                              <div v-else>
                                <v-chip
                                  v-for="(diff, index) in item.details"
                                  :key="index"
                                  small
                                  color="info"
                                  text-color="white"
                                  class="ma-1"
                                >
                                  {{ formatDifference(diff) }}
                                </v-chip>
                                <v-btn
                                  small
                                  text
                                  color="primary"
                                  @click="toggleDifferences(item)"
                                >
                                  Show less
                                  <v-icon small>mdi-chevron-up</v-icon>
                                </v-btn>
                              </div>
                            </div>
                          </div>
                          <span v-else class="text-caption grey--text">No details</span>
                        </template>
                        <template v-slot:item.status>
                          <v-chip
                            :color="results.customerSyncReportOnly ? 'warning' : 'success'"
                            text-color="white"
                            small
                          >
                            {{ results.customerSyncReportOnly ? 'Reported' : 'Fixed' }}
                          </v-chip>
                        </template>
                        <template v-slot:item.actions="{ item }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="{ name: 'Customer', params: { id: item.uid } }"
                          >
                            <v-icon x-small>mdi-account</v-icon>
                          </v-btn>
                        </template>
                      </v-data-table>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>

            <!-- Order Issues Section -->
            <v-row v-if="results.diffs" class="mt-4">
              <v-col cols="12">
                <v-alert type="warning" prominent>
                  <v-row align="center">
                    <v-col class="grow">
                      <div class="text-h6">
                        Order Discrepancies Found
                      </div>
                      <div>
                        {{ Object.keys(results.diffs).length }} customers have meal count mismatches
                      </div>
                    </v-col>
                  </v-row>
                </v-alert>

                <v-expansion-panels class="mt-4">
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div>
                        <v-icon left color="warning">mdi-account-alert</v-icon>
                        View Order Discrepancies ({{ Object.keys(results.diffs).length }})
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-data-table
                        :headers="diffHeaders"
                        :items="formattedDiffs"
                        class="mt-4"
                        dense
                      >
                        <template v-slot:item.uid="{ item }">
                          <div class="d-flex align-center">
                            <v-btn
                              x-small
                              :color="uidFilter === item.uid ? 'accent' : 'secondary'"
                              @click="filterByUid(item.uid)"
                              class="mr-2"
                              :title="uidFilter === item.uid ? 'Clear filter' : 'Filter by UID'"
                            >
                              <v-icon x-small>mdi-filter</v-icon>
                            </v-btn>
                            <code>{{ item.uid }}</code>
                          </div>
                        </template>
                        <template v-slot:item.status="{ item }">
                          <v-chip
                            :color="item.status === 'Mismatch' ? 'error' : 'success'"
                            text-color="white"
                            small
                          >
                            {{ item.status }}
                          </v-chip>
                        </template>
                        <template v-slot:item.actions="{ item }">
                          <v-btn
                            small
                            color="primary"
                            :to="{ name: 'OrderEdit', query: { customer: item.uid, dates: [weekStartDate,  weekEndDate ]} }"
                          >
                            <v-icon small>mdi-receipt</v-icon>
                          </v-btn>
                        </template>
                      </v-data-table>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>

            <!-- Success Message -->
            <v-row v-else class="mt-4">
              <v-col cols="12">
                <v-alert type="success" prominent>
                  <v-row align="center">
                    <v-col class="grow">
                      <div class="text-h6">
                        All Systems Consistent
                      </div>
                      <div>
                        No discrepancies found between Database, Firebase, and Invoices
                      </div>
                    </v-col>
                  </v-row>
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Error Display -->
    <v-row v-if="error">
      <v-col cols="12">
        <v-alert type="error" dismissible @input="error = null">
          <div class="text-h6">Error</div>
          <div>{{ error }}</div>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
          Confirm Fix Mode
        </v-card-title>
        <v-card-text>
          <p>You are about to run the consistency check in <strong>fix mode</strong>.</p>
          <p>This will:</p>
          <ul>
            <li>Check for inconsistencies between Firebase and PostgreSQL</li>
            <li><strong>Automatically fix</strong> any customer sync issues found</li>
            <li>Send Slack notifications about fixes</li>
            <li>Modify data in the database</li>
          </ul>
          <p><strong>Are you sure you want to proceed?</strong></p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="confirmDialog = false">
            Cancel
          </v-btn>
          <v-btn color="warning" @click="runFixMode">
            <v-icon left>mdi-wrench</v-icon>
            Yes, Fix Issues
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from '@/api';
import moment from 'moment';
import urlState from "@/router/urlState";
import localforage from 'localforage';

const CACHE_KEY = 'consistency-check-results';

export default {
  name: 'CheckConsistency',
  mixins: [urlState],
  data() {
    return {
      selectedDate: this.getInitialDate(),
      dateMenu: false,
      loadingReport: false,
      loadingFix: false,
      results: null,
      error: null,
      confirmDialog: false,
      uidFilter: null,
      isCachedResults: false,
      cachedTimestamp: null,
      diffHeaders: [
        {text: 'Customer UID', value: 'uid', sortable: true},
        {text: 'Details', value: 'details', sortable: false},
        {text: 'Status', value: 'status', sortable: true},
        {text: 'Actions', value: 'actions', sortable: false}
      ],
      customerSyncHeaders: [
        {text: 'Customer UID', value: 'uid', sortable: true},
        {text: 'Differences', value: 'differences', sortable: true},
        {text: 'Details', value: 'details', sortable: false},
        {text: 'Status', value: 'status', sortable: true},
        {text: 'Actions', value: 'actions', sortable: false}
      ],
      lineItemAttributeHeaders: [
        {text: 'Customer UID', value: 'uid', sortable: true},
        {text: 'Attribute', value: 'attribute', sortable: true},
        {text: 'Values', value: 'values', sortable: false},
        {text: 'Type', value: 'type', sortable: true},
        {text: 'Actions', value: 'actions', sortable: false}
      ]
    };
  },
  computed: {
    hasIssues() {
      return this.results && this.results.diffs && Object.keys(this.results.diffs).length > 0;
    },
    weekStartDate() {
      return moment(this.selectedDate).startOf('week').format('YYYY-MM-DD');
    },
    weekEndDate() {
      return moment(this.selectedDate).endOf('week').format('YYYY-MM-DD');
    },
    customerSyncColor() {
      if (!this.results || this.results.customerSyncInconsistencies === undefined) return '';
      return this.results.customerSyncInconsistencies > 0 ? 'error--text' : 'success--text';
    },
    formattedDiffs() {
      if (!this.results || !this.results.diffs) return [];

      const formatted = Object.entries(this.results.diffs).map(([uid, details]) => ({
        uid,
        details,
        status: 'Mismatch'
      }));

      // Filter by UID if filter is active
      if (this.uidFilter) {
        return formatted.filter(item => item.uid === this.uidFilter);
      }

      return formatted;
    },
    formattedCustomerSyncDetails() {
      if (!this.results || !this.results.customerSyncDetails) return [];

      const formatted = this.results.customerSyncDetails.map((detail, index) => ({
        uid: detail.uid,
        differences: detail.differences,
        details: detail.differenceDetails || [],
        status: this.results.customerSyncReportOnly ? 'Reported' : 'Fixed',
        expanded: false,
        _id: `customer-sync-${index}` // unique identifier for expand state
      }));

      // Filter by UID if filter is active
      if (this.uidFilter) {
        return formatted.filter(item => item.uid === this.uidFilter);
      }

      return formatted;
    },
    formattedLineItemAttributeDetails() {
      if (!this.results || !this.results.lineItemAttributeDetails) return [];

      const formatted = this.results.lineItemAttributeDetails.map((detail, index) => ({
        uid: detail.uid,
        attribute: detail.attribute,
        customerValue: detail.customerValue,
        lineItemValue: detail.lineItemValue,
        lineItem: detail.lineItem,
        type: detail.type,
        _id: `line-item-attr-${index}`
      }));

      // Filter by UID if filter is active
      if (this.uidFilter) {
        return formatted.filter(item => item.uid === this.uidFilter);
      }

      return formatted;
    }
  },
  methods: {
    getInitialDate() {
      // Get the first Sunday in the future (next Sunday if today is not Sunday, or today if it is Sunday)
      const now = moment();
      const nextSunday = now.clone().startOf('week'); // Sunday is start of week in moment
      
      // If we're past Sunday, get next Sunday
      if (now.day() > 0) {
        nextSunday.add(1, 'week');
      }
      
      return nextSunday.format('YYYY-MM-DD');
    },
    runReportOnly() {
      this.runCheck(true);
    },
    confirmAndFix() {
      this.confirmDialog = true;
    },
    runFixMode() {
      this.confirmDialog = false;
      this.runCheck(false);
    },
    async runCheck(reportOnly = true) {
      if (!this.selectedDate) {
        this.error = 'Please select a date';
        return;
      }

      // Set the appropriate loading state
      if (reportOnly) {
        this.loadingReport = true;
      } else {
        this.loadingFix = true;
      }

      this.error = null;
      this.results = null;
      this.isCachedResults = false;
      this.cachedTimestamp = null;

      try {
        const queryParam = reportOnly ? '?reportOnly=true' : '';
        const response = await api.get(`v2/order/check-firebase/${this.selectedDate}${queryParam}`);
        this.results = response.data;

        // Save results to cache
        await this.saveCachedResults();
      } catch (err) {
        console.error('Error checking line items:', err);
        this.error = err.response?.data?.error || err.message || 'An error occurred while checking line items';
      } finally {
        // Clear the appropriate loading state
        if (reportOnly) {
          this.loadingReport = false;
        } else {
          this.loadingFix = false;
        }
      }
    },
    toggleDifferences(item) {
      this.$set(item, 'expanded', !item.expanded);
    },
    formatDifference(diff) {
      if (!diff) return '';

      // Microdiff uses path (array) not key
      const path = diff.path ? diff.path.join('.') : 'unknown';

      switch (diff.type) {
        case 'CREATE':
          return `+${path}: ${this.truncateValue(diff.value)}`;
        case 'REMOVE':
          return `-${path}`;
        case 'CHANGE':
          return `~${path}: ${this.truncateValue(diff.oldValue)} → ${this.truncateValue(diff.value)}`;
        default:
          return `${path}: ${this.truncateValue(diff.value)}`;
      }
    },
    truncateValue(value) {
      if (value === null || value === undefined) return 'null';
      const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
      return str.length > 20 ? str.substring(0, 20) + '...' : str;
    },
    formatValue(value) {
      if (value === null || value === undefined) return 'null';
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : 'empty array';
      }
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return String(value);
    },
    getOrdersLink(item) {
      // Get the week start date from the line item date
      const weekStartDate = moment(this.selectedDate).startOf('week').format('YYYY-MM-DD');
      const weekEndDate = moment(this.selectedDate).endOf('week').format('YYYY-MM-DD');

      return {
        name: 'search',
        query: {
          search: item.uid,
          dates: [weekStartDate, weekEndDate]
        }
      };
    },
    filterByUid(uid) {
      // Toggle filter: if clicking the same UID, clear the filter
      if (this.uidFilter === uid) {
        this.uidFilter = null;
      } else {
        this.uidFilter = uid;
      }
    },
    async loadCachedResults() {
      try {
        const cached = await localforage.getItem(CACHE_KEY);
        if (cached && cached.results && cached.timestamp && cached.selectedDate) {
          // Only load if cache is less than 24 hours old
          const cacheAge = Date.now() - cached.timestamp;
          const twentyFourHours = 24 * 60 * 60 * 1000;

          if (cacheAge < twentyFourHours) {
            this.results = cached.results;
            this.selectedDate = cached.selectedDate;
            this.isCachedResults = true;
            this.cachedTimestamp = cached.timestamp;
          }
        }
      } catch (err) {
        console.error('Error loading cached results:', err);
      }
    },
    async saveCachedResults() {
      if (!this.results || !this.selectedDate) return;

      try {
        await localforage.setItem(CACHE_KEY, {
          results: this.results,
          selectedDate: this.selectedDate,
          timestamp: Date.now()
        });
      } catch (err) {
        console.error('Error saving cached results:', err);
      }
    },
    formatCacheTime(timestamp) {
      if (!timestamp) return '';
      return moment(timestamp).fromNow();
    }
  },
  async mounted() {
    this.syncToUrl({
      param: 'selectedDate',
      urlParam: 'date',
      initFromRoute: true,
      parseCallback: (v) => {
        if (v && moment(v).isValid()) {
          return moment(v).format('YYYY-MM-DD');
        }
        return this.getInitialDate();
      }
    });

    // Load cached results on mount
    await this.loadCachedResults();
  }
};
</script>

<style scoped>
.text-h4 {
  font-weight: bold;
}

code {
  background-color: #f5f5f5;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
}
</style>